import { useMemo } from "react";

import { BarChart3 } from "lucide-react";
import clsx from "clsx";

import { Chart } from "@/design-system/components/Chart";
import { useMoneySaved } from "./MoneySavedContext";
import { NarrativeCallout } from "./NarrativeCallout";
import { useScrollReveal } from "./useScrollReveal";
import { formatMonthLabel } from "./api";

export const MoneyMonthlySavings = ({
  showNarrative = false,
}: {
  showNarrative?: boolean;
}) => {
  const { months, capiConnectedDate } = useMoneySaved();
  const { ref: chartRef, isVisible } = useScrollReveal(0.3);

  const chartCategories = useMemo(
    () =>
      months.map((item) => {
        const label = formatMonthLabel(item.month);
        return item.is_partial_month ? `${label}*` : label;
      }),
    [months],
  );

  const cpaData = useMemo(() => months.map((item) => item.cpa), [months]);

  const latestCompleteMonth = useMemo(
    () => [...months].reverse().find((m) => !m.is_partial_month),
    [months],
  );

  const baselineCpa = latestCompleteMonth?.baseline_cpa ?? null;
  const latestCpa = latestCompleteMonth?.cpa ?? 0;

  const cpaIsImproving = useMemo(() => {
    if (!baselineCpa || !latestCpa) return false;
    return latestCpa < baselineCpa;
  }, [baselineCpa, latestCpa]);

  const monthsSinceCapi = useMemo(() => {
    if (!capiConnectedDate) return null;
    const connected = new Date(capiConnectedDate);
    const now = new Date();
    const monthCount =
      (now.getFullYear() - connected.getFullYear()) * 12 +
      (now.getMonth() - connected.getMonth());
    return monthCount > 0 ? monthCount : null;
  }, [capiConnectedDate]);

  if (months.length === 0) {
    return (
      <div
        ref={chartRef}
        className={clsx(
          "bg-surface-default rounded-xl border bg-gradient-to-br from-slate-500/5 to-blue-500/10 p-12 shadow-lg transition-all duration-700",
          isVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0",
        )}
      >
        <div className="flex flex-col items-center gap-3 text-center">
          <BarChart3 size={48} className="text-iconMinimal" />
          <p className="text-textSubtle max-w-sm">
            No CPA data available yet. Connect your ad platforms to start
            tracking cost per acquisition.
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      {showNarrative && cpaIsImproving && (
        <NarrativeCallout
          headline="Your cost per acquisition keeps falling."
          subtext={
            monthsSinceCapi
              ? `In the ${monthsSinceCapi} months since connecting CAPI, your CPA has been on a steady downward trend.`
              : "Since connecting CAPI, your CPA has been on a steady downward trend."
          }
        />
      )}
      <div
        ref={chartRef}
        className={clsx(
          "group bg-surface-default rounded-xl border p-6 shadow-lg transition-all duration-700",
          isVisible
            ? "translate-y-0 scale-100 opacity-100 hover:shadow-xl"
            : "translate-y-8 scale-[0.98] opacity-0",
        )}
      >
        <div className="mb-4 flex items-center gap-2">
          <div
            className={clsx(
              "h-8 w-1 rounded-full bg-gradient-to-b from-blue-500 to-purple-500",
              isVisible ? "animate-grow-height" : "scale-y-0",
            )}
            style={{ transformOrigin: "bottom" }}
          />
          <h2
            className={clsx(
              "text-lg font-semibold transition-all duration-500",
              isVisible
                ? "translate-y-0 opacity-100"
                : "translate-y-2 opacity-0",
            )}
            style={{ transitionDelay: isVisible ? "100ms" : "0ms" }}
          >
            Monthly CPA Trend
          </h2>
          <span
            className={clsx(
              "from-surface-subtle via-surface-selected to-surface-subtle text-link ml-auto rounded-full bg-gradient-to-r px-3 py-1 text-xs font-medium transition-all duration-500",
              isVisible
                ? "translate-y-0 opacity-100"
                : "translate-y-2 opacity-0",
            )}
            style={{ transitionDelay: isVisible ? "200ms" : "0ms" }}
          >
            {months.length} months
          </span>
        </div>
        {isVisible && (
          <Chart
            type="line"
            categories={chartCategories}
            series={[
              {
                name: "CPA",
                data: cpaData,
              },
            ]}
            height={280}
            showLegend={!!baselineCpa}
            colors={["#3b82f6"]}
            options={{
              chart: {
                animation: {
                  duration: 1500,
                  easing: "easeOutQuart",
                },
                events: {
                  load: function () {
                    const chart = this as unknown as Highcharts.Chart;
                    const series = chart.series[0];
                    if (!series) return;
                    const points = series.points;
                    const lineDuration = 1500;

                    points.forEach((point, index) => {
                      if (point.graphic) {
                        point.graphic.attr({ opacity: 0 });
                        setTimeout(
                          () => {
                            point.graphic?.animate(
                              { opacity: 1 },
                              { duration: 300, easing: "easeOutQuart" },
                            );
                          },
                          (index / points.length) * lineDuration,
                        );
                      }
                    });

                    const yAxis = chart.yAxis[0] as unknown as {
                      plotLinesAndBands?: {
                        svgElem?: Highcharts.SVGElement;
                      }[];
                    };
                    if (yAxis?.plotLinesAndBands?.length) {
                      const plotLine = yAxis.plotLinesAndBands[0];
                      if (plotLine?.svgElem) {
                        plotLine.svgElem.attr({ opacity: 0 });
                        setTimeout(() => {
                          plotLine.svgElem?.animate(
                            { opacity: 1 },
                            { duration: 500 },
                          );
                        }, lineDuration + 200);
                      }
                    }
                  },
                },
              },
              yAxis: {
                labels: {
                  formatter: function () {
                    return "$" + Number(this.value).toFixed(2);
                  },
                },
                plotLines: baselineCpa
                  ? [
                      {
                        value: baselineCpa,
                        color: "#ef4444",
                        dashStyle: "Dash",
                        width: 2,
                        label: {
                          text: `Baseline CPA: $${baselineCpa.toFixed(2)}`,
                          align: "right",
                          style: {
                            color: "#ef4444",
                            fontWeight: "bold",
                            fontSize: "11px",
                          },
                        },
                      },
                    ]
                  : [],
              },
              tooltip: {
                shared: false,
                formatter: function () {
                  const point = this as unknown as {
                    key: string;
                    y: number;
                  };
                  const label = String(point.key);
                  const isPartial = label.endsWith("*");
                  let html = `<b>${label}</b><br/>CPA: <b>$${point.y?.toFixed(2)}</b>`;
                  if (isPartial) {
                    html += `<br/><i>* Partial data for this month</i>`;
                  }
                  return html;
                },
              },
              plotOptions: {
                series: {
                  animation: {
                    duration: 2000,
                    easing: "easeOutQuart",
                  },
                },
                line: {
                  marker: {
                    enabled: true,
                    radius: 4,
                  },
                },
              },
            }}
          />
        )}
      </div>
    </>
  );
};
